// apps/provider/src/services/api/flowSheetCatalog.js
// Flow sheet item catalog CRUD — shared catalog of trackable items for
// consistent longitudinal data across note types.
//
// Methods: getFlowSheetCatalogItems, getFlowSheetCatalogItem,
//          createFlowSheetCatalogItem, updateFlowSheetCatalogItem,
//          deleteFlowSheetCatalogItem
//
// Response types: @prism-ehr/shared → FlowSheetItemCatalogEntry, FlowSheetItemCatalogListResponse

/**
 * @returns {Object} Flow sheet catalog domain methods (bound to `this` of the composed apiService)
 */
export function createFlowSheetCatalogMethods() {
  return {

    /**
     * Get flow sheet catalog items with optional filters.
     *
     * Xano: GET /flow-sheet-item-catalog
     * Xano staging file: flow_sheet_item_catalog_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.organization_id]
     * @param {string} [filters.category]
     * @param {boolean} [filters.is_active]
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @returns {Promise<import('@prism-ehr/shared').FlowSheetItemCatalogListResponse>}
     */
    getFlowSheetCatalogItems(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      return this.request(`/flow-sheet-item-catalog?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single flow sheet catalog item by ID.
     *
     * Xano: GET /flow-sheet-item-catalog/{item_id}
     * Xano staging file: flow_sheet_item_catalog_item_id_GET.xs
     *
     * @param {string} itemId
     * @returns {Promise<import('@prism-ehr/shared').FlowSheetItemCatalogEntry>}
     */
    getFlowSheetCatalogItem(itemId) {
      return this.request(`/flow-sheet-item-catalog/${itemId}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new flow sheet catalog item.
     *
     * Xano: POST /flow-sheet-item-catalog
     * Xano staging file: flow_sheet_item_catalog_POST.xs
     *
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').FlowSheetItemCatalogEntry>}
     */
    createFlowSheetCatalogItem(data) {
      return this.request('/flow-sheet-item-catalog', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update a flow sheet catalog item.
     *
     * Xano: PATCH /flow-sheet-item-catalog/{item_id}
     * Xano staging file: flow_sheet_item_catalog_item_id_PATCH.xs
     *
     * @param {string} itemId
     * @param {Object} updates
     * @returns {Promise<import('@prism-ehr/shared').FlowSheetItemCatalogEntry>}
     */
    updateFlowSheetCatalogItem(itemId, updates) {
      return this.request(`/flow-sheet-item-catalog/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
    },

    /**
     * Delete a flow sheet catalog item.
     *
     * Xano: DELETE /flow-sheet-item-catalog/{item_id}
     * Xano staging file: flow_sheet_item_catalog_item_id_DELETE.xs
     *
     * @param {string} itemId
     * @returns {Promise<void>}
     */
    deleteFlowSheetCatalogItem(itemId) {
      return this.request(`/flow-sheet-item-catalog/${itemId}`, {
        method: 'DELETE'
      });
    },
  };
}
